import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faDownload } from '@fortawesome/pro-light-svg-icons/faDownload';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { downloadMediaUrl } from '../inlineMediaUrl';
import { captionLength, platformMeta } from '../platformMeta';
import type { CalendarPost } from '../types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel; each part uses its own slice of it */
import styles from './DayPanel.module.scss';
import { trackEvent } from '../../../analytics/sml/analytics';
import { trackAssetDownloaded } from '../assetEvents';
/* eslint-enable css-modules/no-unused-class */

const CONFIRMATION_MS = 2000;

interface ManualShareToolsProps {
  post: CalendarPost;
}

/**
 * The manual posting tools (design b1, they replace the old share modal): the steps, the
 * caption with its count, "Download the file" and "Copy the caption".
 */
const ManualShareToolsComponent: React.FC<ManualShareToolsProps> = ({ post }) => {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const meta = platformMeta[post.platform];

  useEffect(
    () => () => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
      }
    },
    []
  );

  const copy = useCallback(() => {
    navigator.clipboard
      .writeText(post.caption)
      .then(() => {
        setCopied(true);
        trackEvent('link_copied', { kind: 'caption' });
        timer.current = setTimeout(() => setCopied(false), CONFIRMATION_MS);
      })
      .catch(console.error);
  }, [post.caption]);

  return (
    <>
      <ol className={styles.steps}>
        {post.mediaFileUrl === null ? null : (
          <li>
            {intl.formatMessage({ id: 'app.sml.promote.calendar.manual.step1', defaultMessage: 'Download the file' })}
          </li>
        )}
        <li>
          {intl.formatMessage({ id: 'app.sml.promote.calendar.manual.step2', defaultMessage: 'Copy the caption' })}
        </li>
        <li>
          {intl.formatMessage(
            {
              id: 'app.sml.promote.calendar.manual.step3',
              defaultMessage: 'Post it on {platform}, then press "I posted it"',
            },
            { platform: meta.name }
          )}
        </li>
      </ol>
      <div className={styles.caption}>
        {post.caption}
        <span className={styles.ct}>{`${captionLength(post.platform, post.caption)} / ${meta.captionLimit}`}</span>
      </div>
      <div className={styles.row2}>
        {post.mediaFileUrl === null ? null : (
          <a
            className="btn btn-sm btn-outline-primary"
            href={downloadMediaUrl(post.mediaFileUrl)}
            download
            target="_blank"
            rel="noreferrer"
            onClick={() => trackAssetDownloaded(undefined, 'manual')}
          >
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            {intl.formatMessage({
              id: 'app.sml.promote.calendar.manual.download',
              defaultMessage: 'Download the file',
            })}
          </a>
        )}
        <SMLButton size="sm" outline omitCap leftIcon={copied ? faCheck : faCopy} onClick={copy}>
          {copied
            ? intl.formatMessage({ id: 'app.sml.promote.share.copied', defaultMessage: 'Copied' })
            : intl.formatMessage({ id: 'app.sml.promote.calendar.manual.copy', defaultMessage: 'Copy the caption' })}
        </SMLButton>
      </div>
    </>
  );
};

export const ManualShareTools = TypedMemo(ManualShareToolsComponent);
